import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { groupPlanLogin } from "../../api/groupApi";
import { useCSMessage } from "../Common/CSMessage";

// ─── Constants ────────────────────────────────────────────────────────────────

const AFFILIATE_SPONSORS = [
    "Blue Cross Blue Shield of Minnesota",
    "Wellmark Blue Cross Blue Shield of South Dakota",
    "Wellmark Blue Cross Blue Shield of Iowa",
    "Blue Cross Blue Shield of North Dakota",
    "Blue Cross Blue Shield of Nebraska",
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const cardStyle = {
  background: "#fff",
  padding: "30px",
  borderRadius: "12px",
  width: "360px",
  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
  margin: "40px auto",
};

const labelStyle = {
  fontWeight: "bold",
  display: "block",
  marginTop: "10px",
  fontSize: "13px",
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  marginTop: "5px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "14px",
  boxSizing: "border-box",
};

const btnStyle = {
  width: "100%",
  padding: "12px",
  marginTop: "20px",
  background: "#0078d4",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "15px",
};

const errorStyle = {
  color: "red",
  fontSize: "12px",
  marginTop: "3px",
};

const footerStyle = {
  textAlign: "center",
  marginTop: "15px",
  fontSize: "12px",
  color: "#777",
};

// ─── Component ────────────────────────────────────────────────────────────────

const GroupLogin = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onBlur", reValidateMode: "onBlur" });

  const [showMessage, csMessage] = useCSMessage();
  const [isProcessing, setIsProcessing] = useState(false);

  const onSubmit = async (data) => {
    setIsProcessing(true);
    try {
      const response = await groupPlanLogin(data.groupNumber, data.affiliatedSponsor);
      const groupSummaryInfo = response?.groupSummaryInfo ?? response;
      if (!groupSummaryInfo?.GroupNumber) {
        throw new Error("Group Not Found");
      }
      const params = new URLSearchParams({
        groupNumber: data.groupNumber,
        //affiliatedSponsor: data.affiliatedSponsor,
      });
      navigate(`/GroupDashboard?${params}`);
    } catch (error) {
      showMessage({ message: error.message, messageType: "danger" });
      setIsProcessing(false);
    }
  };

  return (
    <div
      role="main"
      className="main-container pt-md-5 pb-5 container js-quickedit-main-content"
      style={isProcessing ? { cursor: "wait" } : undefined}
    >
      <div className="row d-md-flex">
        <section className="col-sm-12 internal-page">
          <div className="region region-content">

            {/* Breadcrumb */}
            <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
              <div className="breadcrumb-wrap">
                <div className="region--breadcrumb nst-1">
                  <section className="block-system block-system-breadcrumb-block">
                    <div role="navigation">
                      <ol className="breadcrumb">
                        <li><Link to="/">Home</Link></li>
                        <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                        <li>Group Login</li>
                      </ol>
                    </div>
                  </section>
                </div>
              </div>
            </section>

            <h1 className="page-header">Group Login</h1>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div style={cardStyle}>

                {/* Group Number */}
                <label htmlFor="groupNumber" style={labelStyle}>
                  Group Number <span aria-hidden="true">*</span>
                </label>
                <input
                  id="groupNumber"
                  placeholder="Enter Group Number"
                  style={inputStyle}
                  {...register("groupNumber", {
                    required: "Group Number is required",
                    validate: (v) =>
                      !/\s/.test(v) || "No spaces allowed",
                  })}
                />
                <ErrorMessage
                  errors={errors}
                  name="groupNumber"
                  render={({ message }) => (
                    <div role="alert" style={errorStyle}>{message}</div>
                  )}
                />

                {/* Affiliated Health Plan Sponsor */}
                <label htmlFor="affiliatedSponsor" style={labelStyle}>
                  Affiliated Health Plan Sponsor <span aria-hidden="true">*</span>
                </label>
                <select
                  id="affiliatedSponsor"
                  style={inputStyle}
                  {...register("affiliatedSponsor", { required: "Affiliated Health Plan Sponsor is required" })}
                >
                  <option value="">Select</option>
                  {AFFILIATE_SPONSORS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ErrorMessage
                  errors={errors}
                  name="affiliatedSponsor"
                  render={({ message }) => (
                    <div role="alert" style={errorStyle}>{message}</div>
                  )}
                />

                {csMessage}

                <button
                  type="submit"
                  style={isProcessing ? { ...btnStyle, opacity: 0.7, cursor: "wait" } : btnStyle}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <div>
                      <span
                        className="spinner-border pmd-spinner spinner-border-sm text-light mr-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Processing...
                    </div>
                  ) : (
                    "Login"
                  )}
                </button>

                {/* <div style={footerStyle}>
                  YourMedicareSolutions &copy; {new Date().getFullYear()}
                </div> */}

              </div>
            </form>

          </div>
        </section>
      </div>
    </div>
  );
};

export default GroupLogin;
